import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "../lib/auth-context";
import { CheckCircle } from "@solar-icons/react";
import { InteractiveParticles } from "../components/InteractiveParticles";
import {
  Logo,
  Button,
  Input,
  Form,
  FormField,
  FormItem,
  FormControl,
  Alert,
  AlertTitle,
  AlertDescription,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from "@pentatonic-ltd/ui";

export const Route = createFileRoute("/signup")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : undefined,
    email: typeof search.email === "string" ? search.email : undefined,
  }),
  component: SignupPage,
});

const PERSONAL_DOMAINS = [
  "gmail.com", "googlemail.com", "yahoo.com", "yahoo.co.uk",
  "hotmail.com", "hotmail.co.uk", "outlook.com", "live.com",
  "msn.com", "icloud.com", "me.com", "mac.com",
  "protonmail.com", "proton.me", "aol.com", "ymail.com",
];

// Assets from Figma design (7-day CDN URLs — swap for local assets in production)
const ASSETS = {
  portrait: "/images/signup-portrait.png",
  kiehls: "/images/kiehls.png",
  bq: "/images/bq.png",
  hyundai: "/images/hyundai.png",
  starbucks: "/images/starbucks.png",
};

function brandFromEmail(email: string): string {
  const domain = email.split("@")[1] ?? "";
  const name = domain.split(".")[0] ?? "";
  return name.charAt(0).toUpperCase() + name.slice(1);
}

function isPersonalEmail(email: string): boolean {
  const domain = email.split("@")[1]?.toLowerCase() ?? "";
  return PERSONAL_DOMAINS.includes(domain);
}

type Step = "form" | "check_email" | "access_requested";

interface SignupFormValues {
  email: string;
  password: string;
  roleType: string;
  country: string;
}

function RightPanel() {
  return (
    <div className="hidden lg:flex flex-1 bg-[#171717] relative overflow-hidden">
      <InteractiveParticles className="absolute inset-0" />
      {/* Brand logos — bottom-centered */}
      <div className="absolute bottom-12 left-0 right-0 flex flex-col gap-6 items-center pointer-events-none z-10">
        <p className="font-mono font-extralight text-[14px] uppercase tracking-[0.28px] text-white/60 text-center">
          Trusted by the world's leading consumer brands
        </p>
        <div className="flex gap-20 items-center">
          {/* Kiehl's: 74×37 */}
          <div className="h-[37px] w-[74px] shrink-0">
            <img src={ASSETS.kiehls} alt="Kiehl's" className="h-full w-full object-contain object-left" />
          </div>
          {/* B&Q: 35×35 */}
          <div className="h-[35px] w-[35px] shrink-0">
            <img src={ASSETS.bq} alt="B&Q" className="h-full w-full object-contain" />
          </div>
          {/* Hyundai: 125×16 */}
          <div className="h-[16px] w-[125px] shrink-0">
            <img src={ASSETS.hyundai} alt="Hyundai" className="h-full w-full object-contain" />
          </div>
          {/* Starbucks: 37×37 */}
          <div className="h-[37px] w-[37px] shrink-0">
            <img src={ASSETS.starbucks} alt="Starbucks" className="h-full w-full object-contain" />
          </div>
        </div>
      </div>
    </div>
  );
}

function LeftPanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-full lg:w-[560px] lg:shrink-0 bg-white dark:bg-[#171717] flex flex-col min-h-screen">
      <div className="flex flex-col flex-1 w-full max-w-[560px] mx-auto">
        {children}
      </div>
    </div>
  );
}

function SignupPage() {
  const navigate = useNavigate();
  const { loginWithSession } = useAuth();
  const { token, email: emailFromUrl } = Route.useSearch();

  const [step, setStep] = useState<Step>("form");
  const [formError, setFormError] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const [pendingEmail, setPendingEmail] = useState("");
  const [pendingTenantName, setPendingTenantName] = useState("");
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent">("idle");
  const [devVerifyUrl, setDevVerifyUrl] = useState<string | null>(null);

  const verifyingRef = useRef(false);

  // Auto-verify when landing with ?token= in URL (from email link)
  useEffect(() => {
    if (!token || verifyingRef.current) return;
    verifyingRef.current = true;
    setStep("check_email");

    (async () => {
      try {
        const response = await fetch("/api/v1/auth/verify-code", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const result = await response.json() as {
          success: boolean;
          error?: string;
          data?: {
            sessionId?: string;
            user?: { name: string; isAdmin: boolean; tenant: { id: string; name: string; slug: string; plan: string }; tenants: { id: string; name: string; slug: string; plan: string }[] };
            expiresAt?: string;
            requestId?: string;
          };
        };
        if (result.success) {
          if (result.data?.sessionId) {
            // Owner path — email verified, log straight in
            loginWithSession(result.data as Parameters<typeof loginWithSession>[0]);
            navigate({ to: "/market-intelligence" });
          } else {
            // Access request path — pending admin approval
            setStep("access_requested");
          }
        } else {
          setVerifyError("This verification link is invalid or has expired. Go back and sign up again with the same email to get a fresh link.");
        }
      } catch {
        setVerifyError("Something went wrong. Please try again.");
      }
    })();
  }, [token]);

  const signupForm = useForm<SignupFormValues>({
    defaultValues: { email: "", roleType: "", country: "", password: "" },
  });

  const handleResend = async () => {
    const emailToResend = pendingEmail || emailFromUrl;
    if (!emailToResend || resendState === "sending") return;
    setResendState("sending");
    try {
      await fetch("/api/v1/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailToResend }),
      });
    } catch {
      // Always show success to avoid enumeration
    }
    setResendState("sent");
    setVerifyError(null);
  };

  const handleSignup = async (values: SignupFormValues) => {
    setFormError(null);

    if (isPersonalEmail(values.email)) {
      signupForm.setError("email", { message: "Work email required" });
      setFormError("Please use your work email address to sign up.");
      return;
    }

    try {
      const response = await fetch("/api/v1/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });

      const result = (await response.json()) as {
        success: boolean;
        error?: string;
        data?: {
          sessionId?: string;
          user?: {
            name: string;
            isAdmin: boolean;
            tenant: { id: string; name: string; slug: string; plan: string };
            tenants: { id: string; name: string; slug: string; plan: string }[];
          };
          expiresAt?: string;
          status?: string;
          tenantName?: string;
          verificationToken?: string; // dev mode only — auto-verified immediately
        };
      };

      if (!result.success) {
        const err = result.error ?? "registration_failed";
        if (err === "collision") {
          setFormError(
            "Your brand is already on Pentatonic. Contact hello@pentatonic.com to get access."
          );
        } else if (err === "Email already registered") {
          signupForm.setError("email", { message: "Email already registered" });
          setFormError("This email is already registered. Try signing in instead.");
        } else if (err === "Access request already pending") {
          setFormError(
            "You've already requested access. Check your email for the verification code."
          );
        } else {
          setFormError("Something went wrong. Please try again.");
        }
        return;
      }

      if (result.data?.status === "check_email" || result.data?.status === "access_requested") {
        setPendingEmail(values.email);
        setPendingTenantName(
          result.data.tenantName ?? brandFromEmail(values.email)
        );
        if (result.data.verificationToken) {
          setDevVerifyUrl(`/signup?token=${result.data.verificationToken}&email=${encodeURIComponent(values.email)}`);
        }
        setStep("check_email");
        return;
      }
    } catch {
      setFormError("Something went wrong. Please try again.");
    }
  };

  // ── Step: form ──────────────────────────────────────────────────────────────

  if (step === "form") {
    return (
      <div className="min-h-screen w-full flex bg-[#171717]">
        <LeftPanel>
          {/* Logo — pinned top */}
          <div className="px-10 pt-12 shrink-0">
            <a href="/"><Logo variant="auto" size="lg" /></a>
          </div>

          {/* Form — vertically centred */}
          <div className="flex-1 flex flex-col px-10 pb-10">
            <div className="flex flex-col gap-8 pt-20">
              <div className="flex flex-col gap-2">
                <h1 className="text-[29px] leading-[1.4] font-light text-[#171717] dark:text-white">
                  Sign up to get started
                </h1>
                <p className="text-[18px] leading-[1.4] font-light text-[#A9A9A9]">
                  Tell us about you
                </p>
              </div>

                <Form
                  form={signupForm}
                  onSubmit={handleSignup}
                  className="flex flex-col gap-6"
                >
                  {formError && (
                    <Alert variant="error">
                      <AlertTitle>Can't continue</AlertTitle>
                      <AlertDescription>{formError}</AlertDescription>
                    </Alert>
                  )}

                  <FormField
                    control={signupForm.control}
                    name="email"
                    rules={{ required: "Email address is required" }}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            type="email"
                            label="Email address"
                            variant={fieldState.error ? "error" : "default"}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={signupForm.control}
                    name="password"
                    rules={{ required: "Required", minLength: 8 }}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            passwordToggle
                            label="Password"
                            variant={fieldState.error ? "error" : "default"}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />


                  <FormField
                    control={signupForm.control}
                    name="roleType"
                    rules={{ required: "Role type is required" }}
                    render={({ field, fieldState }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger
                          label="Your role type"
                          variant={fieldState.error ? "error" : "default"}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="brand-manager">Brand Manager</SelectItem>
                          <SelectItem value="sustainability-lead">Sustainability Lead</SelectItem>
                          <SelectItem value="ecommerce-manager">E-commerce Manager</SelectItem>
                          <SelectItem value="marketing-manager">Marketing Manager</SelectItem>
                          <SelectItem value="product-director">Product Director</SelectItem>
                          <SelectItem value="ceo-founder">CEO / Founder</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />

                  <FormField
                    control={signupForm.control}
                    name="country"
                    rules={{ required: "Country is required" }}
                    render={({ field, fieldState }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger
                          label="Country"
                          variant={fieldState.error ? "error" : "default"}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="gb">United Kingdom</SelectItem>
                          <SelectItem value="us">United States</SelectItem>
                          <SelectItem value="de">Germany</SelectItem>
                          <SelectItem value="fr">France</SelectItem>
                          <SelectItem value="it">Italy</SelectItem>
                          <SelectItem value="es">Spain</SelectItem>
                          <SelectItem value="nl">Netherlands</SelectItem>
                          <SelectItem value="se">Sweden</SelectItem>
                          <SelectItem value="no">Norway</SelectItem>
                          <SelectItem value="dk">Denmark</SelectItem>
                          <SelectItem value="au">Australia</SelectItem>
                          <SelectItem value="ca">Canada</SelectItem>
                          <SelectItem value="jp">Japan</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    loading={signupForm.formState.isSubmitting}
                  >
                    Create account
                  </Button>
                </Form>
            </div>
          </div>

          {/* Bottom link — pinned bottom */}
          <div className="px-10 pb-10 shrink-0">
            <p className="text-[14px] text-[#A9A9A9]">
              Already have an account?{" "}
              <a href="/" className="text-[#171717] dark:text-white underline underline-offset-2">
                Sign in →
              </a>
            </p>
          </div>
        </LeftPanel>
        <RightPanel />
      </div>
    );
  }

  // ── Step: check_email ───────────────────────────────────────────────────────

  if (step === "check_email") {
    return (
      <div className="min-h-screen w-full flex bg-[#171717]">
        <LeftPanel>
          <div className="px-10 pt-12 shrink-0">
            <a href="/"><Logo variant="auto" size="lg" /></a>
          </div>

          <div className="flex-1 flex flex-col px-10 pb-10">
            <div className="flex flex-col gap-8 pt-20">
              <div className="flex flex-col gap-2">
                <h1 className="text-[29px] leading-[1.4] font-light text-[#171717] dark:text-white">
                  Check your email
                </h1>
                <p className="text-[18px] leading-[1.4] font-light text-[#A9A9A9]">
                  We sent a verification link to{" "}
                  <span className="text-[#171717] dark:text-white">{pendingEmail || "your email"}</span>
                  . Click it to confirm you work at {pendingTenantName || "your brand"}.
                </p>
              </div>

              {devVerifyUrl && (
                <a
                  href={devVerifyUrl}
                  className="text-[13px] underline underline-offset-2 text-[#A9A9A9] break-all"
                >
                  Dev: {window.location.origin}{devVerifyUrl}
                </a>
              )}

              {token && (
                <div className="flex items-center gap-3 text-[#A9A9A9]">
                  <Spinner size="sm" />
                  <span className="text-[14px]">Verifying…</span>
                </div>
              )}


              {verifyError && resendState !== "sent" && (
                <Alert variant="error">
                  <AlertTitle>Link not accepted</AlertTitle>
                  <AlertDescription>
                    {verifyError}{" "}
                    {(pendingEmail || emailFromUrl) && (
                      <button
                        type="button"
                        onClick={handleResend}
                        disabled={resendState === "sending"}
                        className="underline underline-offset-2 font-medium"
                      >
                        {resendState === "sending" ? "Sending…" : "Resend verification email →"}
                      </button>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {resendState === "sent" && (
                <Alert variant="success">
                  <AlertTitle>Email sent</AlertTitle>
                  <AlertDescription>
                    We've sent a fresh verification link to{" "}
                    <span className="font-medium">{pendingEmail || emailFromUrl}</span>.
                    Check your inbox.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          <div className="px-10 pb-10 shrink-0">
            <button
              className="text-[14px] underline underline-offset-2 text-[#171717] dark:text-white cursor-pointer"
              onClick={() => { setStep("form"); setVerifyError(null); }}
            >
              ← Back
            </button>
          </div>
        </LeftPanel>
        <RightPanel />
      </div>
    );
  }

  // ── Step: access_requested ─────────────────────────────────────────────────

  return (
    <div className="min-h-screen w-full flex bg-[#171717]">
      <LeftPanel>
        <div className="px-10 pt-12 shrink-0">
          <a href="/"><Logo variant="auto" size="lg" /></a>
        </div>

        <div className="flex-1 flex flex-col px-10 pb-10">
          <div className="flex flex-col gap-8 pt-20">
            <CheckCircle size={48} color="#171717" weight="LineDuotone" />
            <div className="flex flex-col gap-2">
              <h1 className="text-[29px] leading-[1.4] font-light text-[#171717] dark:text-white">
                Request sent
              </h1>
              <p className="text-[18px] leading-[1.4] font-light text-[#A9A9A9]">
                Your email is verified. The account owner at{" "}
                <span className="text-[#171717] dark:text-white">{pendingTenantName}</span>{" "}
                will review your request and you'll be able to sign in once approved.
              </p>
            </div>
          </div>
        </div>

        <div className="px-10 pb-10 shrink-0">
          <a href="/" className="text-[14px] text-[#171717] dark:text-white underline underline-offset-2">
            ← Back to sign in
          </a>
        </div>
      </LeftPanel>
      <RightPanel />
    </div>
  );
}
